/**
 * PRD.330 Phase 2 — Report UI interaction state (pure, node:test-able).
 *
 * The ONLY applied state is the Phase 1 `ReportQuery` from @helios/domain.
 * This module adds just the transient UI state around it — which column's
 * filter popover is open and its local draft — and routes every change to
 * the applied query through the Phase 1 helpers (`validateReportQuery`,
 * `clearFilters`, `clearColumnFilter`, `normalizeValuesSelection`,
 * `buildRangeFilterFromDraft`, …). Query semantics are never re-implemented
 * here, and nothing in this module ever sees report rows.
 */
import {
  buildRangeFilterFromDraft,
  clampReportPage,
  clearColumnFilter,
  clearFilters,
  effectiveSort,
  normalizeValuesSelection,
  resetReport,
  resolveReportDatePreset,
  validateReportQuery,
  type ReportColumnFilter,
  type ReportDatePresetId,
  type ReportDateRange,
  type ReportDefinition,
  type ReportQuery,
  type ReportSortDirection,
} from '@helios/domain';

// ─── State ───

export type ReportFilterDraft =
  | { kind: 'text'; value: string }
  | { kind: 'values'; selected: string[]; optionSearch: string }
  | { kind: 'range'; min: string; max: string };

/**
 * PRD.330 Phase 4 — which date control produced `query.dateRange`. UI state
 * only: it never enters ReportQuery or SQL. A manual edit is always Custom,
 * even when the dates happen to equal a preset.
 */
export type ReportDateSelection = { kind: 'preset'; id: ReportDatePresetId } | { kind: 'custom' };

export interface ReportUiState<C extends string> {
  /** The applied, canonical report query (Phase 1 contract). */
  query: ReportQuery<C>;
  dateSelection: ReportDateSelection;
  /**
   * Time-series drill-down history (UI state only). Each entry is the date
   * range + selection to return to with "Back". Cleared by any explicit date
   * choice (preset, manual From/To, Reset).
   */
  dateDrillStack: ReadonlyArray<{ dateRange: ReportDateRange; dateSelection: ReportDateSelection }>;
  /** Column whose filter popover is open, if any. */
  openColumn: C | null;
  /** Local draft for the open popover — never part of the applied query. */
  draft: ReportFilterDraft | null;
  /** Validation feedback for the open draft (Apply rejected). */
  draftError: string | null;
  /** Validation feedback for the report-level date range. */
  dateError: string | null;
}

export type ReportUiAction<C extends string> =
  | { type: 'openFilter'; columnId: C; facetValues: ReadonlyArray<string> }
  | { type: 'editDraft'; draft: ReportFilterDraft }
  | { type: 'selectVisible'; visibleValues: ReadonlyArray<string> }
  | { type: 'clearVisible'; visibleValues: ReadonlyArray<string> }
  | { type: 'applyDraft'; facetValues: ReadonlyArray<string> }
  | { type: 'cancelDraft' }
  | { type: 'clearColumn'; columnId: C }
  | { type: 'setColumnFilter'; columnId: C; filter: ReportColumnFilter | undefined }
  | { type: 'clearFilters' }
  | { type: 'toggleSort'; columnId: C }
  | { type: 'setSort'; columnId: C; direction: ReportSortDirection }
  | { type: 'setSearch'; search: string }
  | { type: 'setDateRange'; from: string; to: string }
  | { type: 'selectDatePreset'; id: ReportDatePresetId; now?: Date }
  | { type: 'selectCustomDate' }
  | { type: 'drillDateRange'; range: ReportDateRange }
  | { type: 'drillBack' }
  | { type: 'setPage'; page: number }
  | { type: 'clampPage'; totalCount: number }
  | { type: 'reset'; now?: Date };

export function initialReportUiState<C extends string>(
  query: ReportQuery<C>,
  dateSelection: ReportDateSelection = { kind: 'custom' },
): ReportUiState<C> {
  return {
    query,
    dateSelection,
    dateDrillStack: [],
    openColumn: null,
    draft: null,
    draftError: null,
    dateError: null,
  };
}

/** The definition's default state: its default preset resolved for `now`. */
export function defaultReportUiState<C extends string>(
  definition: ReportDefinition<C>,
  now: Date = new Date(),
): ReportUiState<C> {
  return initialReportUiState(resetReport(definition, now), {
    kind: 'preset',
    id: definition.defaultDatePreset,
  });
}

// ─── Draft helpers ───

/** Cents → the major-unit text shown in a range input (e.g. 50000 → "500.00"). */
export function centsToAmountInput(cents: number | undefined): string {
  if (cents === undefined) return '';
  return (cents / 100).toFixed(2);
}

/**
 * Seed a popover draft from the APPLIED filter. With no values filter
 * applied, every supplied option starts checked (all = no filter).
 */
export function draftFromApplied(
  kind: 'text' | 'values' | 'range',
  applied: ReportColumnFilter | undefined,
  facetValues: ReadonlyArray<string>,
): ReportFilterDraft {
  switch (kind) {
    case 'text':
      return { kind: 'text', value: applied?.kind === 'text' ? applied.value : '' };
    case 'values':
      return {
        kind: 'values',
        selected: applied?.kind === 'values' ? [...applied.values] : [...facetValues],
        optionSearch: '',
      };
    case 'range':
      return {
        kind: 'range',
        min: applied?.kind === 'range' ? centsToAmountInput(applied.min) : '',
        max: applied?.kind === 'range' ? centsToAmountInput(applied.max) : '',
      };
  }
}

/** Select All (matches): add the visible options; selections outside the search are kept. */
export function selectVisible(
  selected: ReadonlyArray<string>,
  visible: ReadonlyArray<string>,
): string[] {
  const next = new Set(selected);
  for (const v of visible) next.add(v);
  return [...next];
}

/** Clear All (matches): remove only the visible options; others are kept. */
export function clearVisible(
  selected: ReadonlyArray<string>,
  visible: ReadonlyArray<string>,
): string[] {
  const drop = new Set(visible);
  return selected.filter((v) => !drop.has(v));
}

/**
 * Resolve a draft into the column's next applied filter.
 * `undefined` = remove the column filter; `{ error }` = reject Apply.
 */
export function resolveDraft(
  draft: ReportFilterDraft,
  facetValues: ReadonlyArray<string>,
): { filter: ReportColumnFilter | undefined } | { error: string } {
  switch (draft.kind) {
    case 'text': {
      const value = draft.value.trim();
      return { filter: value === '' ? undefined : { kind: 'text', value } };
    }
    case 'values': {
      const normalized = normalizeValuesSelection({
        selected: draft.selected,
        allOptionValues: facetValues,
        optionSearch: draft.optionSearch,
      });
      if (normalized === 'empty') return { error: 'Select at least one value, or use Clear.' };
      return { filter: normalized ?? undefined };
    }
    case 'range': {
      const result = buildRangeFilterFromDraft(draft.min, draft.max);
      if (result.kind === 'error') return { error: result.message };
      return { filter: result.kind === 'none' ? undefined : result.filter };
    }
  }
}

/**
 * PRD.330 Phase 3 — a facet popover opened before its options arrived was
 * seeded with nothing checked. Once options load, re-seed it IF the user has
 * not touched the draft yet and no filter is applied for that column.
 */
export function shouldReseedFacetDraft<C extends string>(state: ReportUiState<C>): boolean {
  return (
    state.openColumn !== null &&
    state.draft?.kind === 'values' &&
    state.draft.selected.length === 0 &&
    state.draft.optionSearch === '' &&
    state.query.filters[state.openColumn] === undefined
  );
}

// ─── Reducer ───

function withQuery<C extends string>(
  state: ReportUiState<C>,
  definition: ReportDefinition<C>,
  next: ReportQuery<C>,
): ReportUiState<C> | null {
  // Every applied change passes Phase 1 validation, or it is not applied.
  return validateReportQuery(definition, next).length === 0 ? { ...state, query: next } : null;
}

function closed<C extends string>(state: ReportUiState<C>): ReportUiState<C> {
  return { ...state, openColumn: null, draft: null, draftError: null };
}

export function reportUiReducer<C extends string>(
  definition: ReportDefinition<C>,
  state: ReportUiState<C>,
  action: ReportUiAction<C>,
): ReportUiState<C> {
  switch (action.type) {
    case 'openFilter': {
      const column = definition.columns.find((c) => c.id === action.columnId);
      if (!column || column.filter === null) return state;
      return {
        ...state,
        openColumn: action.columnId,
        draft: draftFromApplied(
          column.filter,
          state.query.filters[action.columnId],
          action.facetValues,
        ),
        draftError: null,
      };
    }

    case 'editDraft':
      if (!state.draft || state.draft.kind !== action.draft.kind) return state;
      return { ...state, draft: action.draft, draftError: null };

    case 'selectVisible':
    case 'clearVisible': {
      if (state.draft?.kind !== 'values') return state;
      const selected =
        action.type === 'selectVisible'
          ? selectVisible(state.draft.selected, action.visibleValues)
          : clearVisible(state.draft.selected, action.visibleValues);
      return { ...state, draft: { ...state.draft, selected }, draftError: null };
    }

    case 'applyDraft': {
      if (state.openColumn === null || !state.draft) return state;
      const resolved = resolveDraft(state.draft, action.facetValues);
      if ('error' in resolved) return { ...state, draftError: resolved.error };
      const filters = { ...state.query.filters };
      if (resolved.filter === undefined) delete filters[state.openColumn];
      else filters[state.openColumn] = resolved.filter;
      const next = withQuery(state, definition, { ...state.query, filters, page: 1 });
      if (!next) return { ...state, draftError: 'This filter is not valid.' };
      return closed(next);
    }

    case 'cancelDraft':
      return closed(state);

    case 'clearColumn':
      return closed({ ...state, query: clearColumnFilter(state.query, action.columnId) });

    case 'setColumnFilter': {
      // PRD.330 Phase 5 follow-up — an explicit, immediate filter change
      // (e.g. a chart category click). Writes the canonical applied query
      // directly (no popover draft), validated like any other change; only
      // this column's filter changes, and the page returns to 1.
      const filters = { ...state.query.filters };
      if (action.filter === undefined) delete filters[action.columnId];
      else filters[action.columnId] = action.filter;
      return withQuery(state, definition, { ...state.query, filters, page: 1 }) ?? state;
    }

    case 'clearFilters':
      return closed({ ...state, query: clearFilters(state.query) });

    case 'toggleSort': {
      const column = definition.columns.find((c) => c.id === action.columnId);
      if (!column?.sortable) return state;
      const current = effectiveSort(definition, state.query);
      const firstDirection: ReportSortDirection =
        definition.defaultSort.columnId === action.columnId
          ? definition.defaultSort.direction
          : 'asc';
      let sort: ReportQuery<C>['sort'];
      if (current.columnId !== action.columnId) {
        sort = { columnId: action.columnId, direction: firstDirection };
      } else if (current.direction === firstDirection) {
        sort = { columnId: action.columnId, direction: firstDirection === 'asc' ? 'desc' : 'asc' };
      } else {
        sort = null; // back to the report default sort
      }
      return withQuery(state, definition, { ...state.query, sort, page: 1 }) ?? state;
    }

    case 'setSort':
      return (
        withQuery(state, definition, {
          ...state.query,
          sort: { columnId: action.columnId, direction: action.direction },
          page: 1,
        }) ?? state
      );

    case 'setSearch':
      if (action.search === state.query.search) return state;
      return (
        withQuery(state, definition, { ...state.query, search: action.search, page: 1 }) ?? state
      );

    case 'setDateRange': {
      const next = withQuery(state, definition, {
        ...state.query,
        dateRange: { from: action.from, to: action.to },
        page: 1,
      });
      if (!next) {
        const message =
          action.from && action.to && action.from > action.to
            ? 'Date From must be on or before Date To.'
            : 'Enter a valid date.';
        return { ...state, dateError: message };
      }
      // A manual edit is always Custom (never auto-recognized as a preset).
      return { ...next, dateError: null, dateSelection: { kind: 'custom' }, dateDrillStack: [] };
    }

    case 'selectDatePreset': {
      if (!definition.datePresets.includes(action.id)) return state;
      const range = resolveReportDatePreset(action.id, action.now ?? new Date());
      const next = withQuery(state, definition, { ...state.query, dateRange: range, page: 1 });
      if (!next) return state;
      return {
        ...next,
        dateError: null,
        dateSelection: { kind: 'preset', id: action.id },
        dateDrillStack: [],
      };
    }

    case 'drillDateRange': {
      // Drill into a time-series period: the canonical date range becomes that
      // period (Custom); every projection follows. Other filters, search and
      // sort are kept; the current range is saved for "Back".
      const next = withQuery(state, definition, {
        ...state.query,
        dateRange: { from: action.range.from, to: action.range.to },
        page: 1,
      });
      if (!next) return state;
      return {
        ...next,
        dateError: null,
        dateSelection: { kind: 'custom' },
        dateDrillStack: [
          ...state.dateDrillStack,
          { dateRange: state.query.dateRange, dateSelection: state.dateSelection },
        ],
      };
    }

    case 'drillBack': {
      const previous = state.dateDrillStack[state.dateDrillStack.length - 1];
      if (!previous) return state;
      const next = withQuery(state, definition, {
        ...state.query,
        dateRange: previous.dateRange,
        page: 1,
      });
      if (!next) return state;
      return {
        ...next,
        dateError: null,
        dateSelection: previous.dateSelection,
        dateDrillStack: state.dateDrillStack.slice(0, -1),
      };
    }

    case 'selectCustomDate':
      // Custom keeps the current range; it only marks the dates as user-chosen.
      return state.dateSelection.kind === 'custom'
        ? state
        : { ...state, dateSelection: { kind: 'custom' } };

    case 'setPage':
      return withQuery(state, definition, { ...state.query, page: action.page }) ?? state;

    case 'clampPage': {
      const page = clampReportPage(state.query.page, action.totalCount, definition.pageSize);
      return page === state.query.page ? state : { ...state, query: { ...state.query, page } };
    }

    case 'reset':
      return defaultReportUiState(definition, action.now);
  }
}
